"use client";

import { type ClipboardEvent, type KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { AuthPanel } from "~/components/auth/AuthPanel";
import { Button } from "~/components/ui/button";
import { Paragraph } from "~/components/ui/Typography";
import { cn } from "~/lib/utils";
type OtpVerificationProps = {
  identifier: string;
  channel: "phone" | "email";
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
};
const OTP_LENGTH = 6;
const SLOT_KEYS = ["s0", "s1", "s2", "s3", "s4", "s5"] as const;
const RESEND_COOLDOWN = 60;
export function OtpVerification({
  identifier,
  channel,
  onVerify,
  onResend,
  onBack
}: OtpVerificationProps) {
  // One entry per slot — each box owns its own digit, like Uber's pin-code.
  const [digits, setDigits] = useState<string[]>(() => Array(OTP_LENGTH).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const verifyingRef = useRef(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Countdown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Focus the first slot on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);
  const focusSlot = useCallback((index: number) => {
    const target = inputRefs.current[index];
    target?.focus();
    target?.select();
  }, []);
  const resetSlots = useCallback(() => {
    setDigits(Array(OTP_LENGTH).fill(""));
  }, []);

  // When an error appears the slots are cleared and the inputs were disabled
  // mid-verify. Drive focus back to the first slot from an effect so it runs
  // after the re-render that re-enables the inputs — focusing a disabled input
  // is a no-op, which is why a synchronous focus would land on the wrong slot.
  useEffect(() => {
    if (error) focusSlot(0);
  }, [error, focusSlot]);
  const submitCode = useCallback(async (value: string) => {
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setError(null);
    setIsVerifying(true);
    try {
      await onVerify(value);
      // Success unmounts this step (advance or navigate), so keep the spinner
      // up through the transition. Only clear it on error to allow a retry.
    } catch (err) {
      const message = err instanceof Error ? err.message : "The code you've entered is incorrect.";
      setError(message);
      // Wrong code wipes everything and restarts from the first slot.
      resetSlots();
      verifyingRef.current = false;
      setIsVerifying(false);
    }
  }, [onVerify, resetSlots]);

  // Commit a freshly-filled set of digits: update state and auto-submit once
  // every slot is populated.
  const commitDigits = useCallback((next: string[]) => {
    setDigits(next);
    const joined = next.join("");
    if (joined.length === OTP_LENGTH) {
      void submitCode(joined);
    }
  }, [submitCode]);
  const handleChange = useCallback((index_0: number, raw: string) => {
    const numeric = raw.replace(/\D/g, "");
    if (numeric.length === 0) return;
    // Typing again after a failure clears the error.
    setError(null);
    const next_0 = [...digits];
    // Spread typed/autofilled characters across this slot and the ones after.
    let cursor = index_0;
    for (const char of numeric) {
      if (cursor >= OTP_LENGTH) break;
      next_0[cursor] = char;
      cursor += 1;
    }
    commitDigits(next_0);

    // Advance by the number of characters consumed, capped at the last slot.
    focusSlot(Math.min(cursor, OTP_LENGTH - 1));
  }, [digits, commitDigits, focusSlot]);
  const handleKeyDown = useCallback((index_1: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      setError(null);
      const next_1 = [...digits];
      if (next_1[index_1]) {
        // Clear the current slot but stay put.
        next_1[index_1] = "";
        setDigits(next_1);
      } else if (index_1 > 0) {
        // Already empty — step back and clear the previous slot.
        next_1[index_1 - 1] = "";
        setDigits(next_1);
        focusSlot(index_1 - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft" && index_1 > 0) {
      e.preventDefault();
      focusSlot(index_1 - 1);
      return;
    }
    if (e.key === "ArrowRight" && index_1 < OTP_LENGTH - 1) {
      e.preventDefault();
      focusSlot(index_1 + 1);
    }
  }, [digits, focusSlot]);
  const handlePaste = useCallback((index_2: number, e_0: ClipboardEvent<HTMLInputElement>) => {
    e_0.preventDefault();
    const pasted = e_0.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH - index_2);
    if (pasted.length === 0) return;
    setError(null);
    const next_2 = [...digits];
    let cursor_0 = index_2;
    for (const char_0 of pasted) {
      next_2[cursor_0] = char_0;
      cursor_0 += 1;
    }
    commitDigits(next_2);
    const nextEmpty = next_2.indexOf("");
    focusSlot(nextEmpty === -1 ? OTP_LENGTH - 1 : nextEmpty);
  }, [digits, commitDigits, focusSlot]);
  const handleResend = async () => {
    setIsResending(true);
    setError(null);
    try {
      await onResend();
      resetSlots();
      setResendCooldown(RESEND_COOLDOWN);
      setTimeout(() => focusSlot(0), 0);
    } catch (err_0) {
      const message_0 = err_0 instanceof Error ? err_0.message : "Failed to resend code.";
      setError(message_0);
    } finally {
      setIsResending(false);
    }
  };
  const code = digits.join("");
  const handleSubmit = () => {
    // The Verify button is disabled until all slots are filled, so this is
    // only a defensive guard against an incomplete submit.
    if (code.length !== OTP_LENGTH) return;
    void submitCode(code);
  };
  return <AuthPanel title="Enter verification code" subtitle={`We sent a 6-digit code to ${identifier}`}>
      <div className="mb-4 flex w-full justify-center gap-2">
        {SLOT_KEYS.map((key, i) => <input key={key} ref={el => {
        inputRefs.current[i] = el;
      }} type="text" inputMode="numeric" pattern="\d*" autoComplete={i === 0 ? "one-time-code" : "off"} value={digits[i] ?? ""} onChange={e_1 => handleChange(i, e_1.target.value)} onKeyDown={e_2 => handleKeyDown(i, e_2)} onPaste={e_3 => handlePaste(i, e_3)} onFocus={e_4 => e_4.target.select()} disabled={isVerifying} aria-label={`Verification code digit ${i + 1} of ${OTP_LENGTH}`} aria-invalid={error ? true : undefined} className={cn("h-14 w-12 rounded-xl border bg-surface-2 text-center text-xl font-semibold text-body caret-accent", "transition-colors outline-none", "focus:border-accent focus:bg-surface", isVerifying && "opacity-50", error ? "border-danger" : "border-border")} />)}
      </div>

      {error ? <div role="alert" className="mb-3">
          <Paragraph size="sm" className="text-center text-danger">
            {error}
          </Paragraph>
        </div> : null}

      <Button className="mb-3 w-full" variant="primary" size="lg" disabled={isVerifying || code.length !== OTP_LENGTH} onClick={handleSubmit} type="button">
        {isVerifying ? "Verifying..." : "Verify"}
      </Button>

      <div className="flex flex-col items-center gap-2">
        <button type="button" disabled={resendCooldown > 0 || isResending} onClick={handleResend} className={cn("text-[13px] font-semibold transition-colors", resendCooldown > 0 || isResending ? "cursor-not-allowed text-muted" : "cursor-pointer text-accent-dark hover:underline")}>
          {isResending ? "Sending..." : resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : "Resend code"}
        </button>

        <Button variant="ghost" size="sm" onClick={onBack} type="button">
          {channel === "phone" ? "Change phone number" : "Change email address"}
        </Button>
      </div>
    </AuthPanel>;
}